import { cardLabel, newDecks, rankOf, type Card } from '../cards';
import type { Rng } from '../rng';
import { luckOf, secondChanceFrame } from './luck';
import type { ActResult, CasinoGame, TableEnv, TableOption } from './types';

/**
 * Six-deck blackjack. Dealer stands on soft 17, blackjack pays 3:2, dealer
 * peeks for blackjack, double on any first two cards (including after a
 * split), one split allowed, split aces get one card each. No insurance or
 * surrender. The house edge comes out around 0.5-1% against good play —
 * see blackjack.test.ts.
 */
const DECKS = 6;
/** Reshuffle before a deal once fewer than this many cards remain. */
const RESHUFFLE_BELOW = 78;
/** The ★'s luck, by luck level: how often a card that would bust you is
 *  swapped for another, and how often a dealer hand that beats you (or a
 *  dealer blackjack) is re-dealt. Every second chance is shown, saved or
 *  not (user, 2026-09-23: the ★ didn't feel lucky — it used to save only
 *  ~1 hand in 58, never a bust, and hid the re-deals that failed).
 *  Calibrated by sim (basic strategy) to 103% / 110%; pinned in
 *  luck.test.ts. */
export const SECOND_CHANCE = [0, 0.105, 0.3];

export interface BjHand {
  cards: Card[];
  bet: number;
  done: boolean;
  /** Split aces get exactly one card and can't be hit. */
  splitAces: boolean;
}

export interface BlackjackState {
  phase: 'idle' | 'player' | 'done';
  shoe: Card[];
  dealer: Card[];
  hands: BjHand[];
  active: number;
  /** Outcome lines for the finished hand. */
  results: string[];
  net: number;
  /** The table's luck for this hand (★ luck re-deals a winning dealer). */
  luck?: number;
  /** A second chance saved you this hand. */
  lucky?: boolean;
}

export function cardValue(card: Card): number {
  const r = rankOf(card);
  if (r === 0) return 11;
  return Math.min(r + 1, 10);
}

export function handTotal(cards: Card[]): { total: number; soft: boolean } {
  let total = 0;
  let aces = 0;
  for (const c of cards) {
    total += cardValue(c);
    if (rankOf(c) === 0) aces++;
  }
  while (total > 21 && aces > 0) {
    total -= 10;
    aces--;
  }
  return { total, soft: aces > 0 };
}

/** A♠ + J♠ (any deck in the shoe) — the progressive-jackpot hand. */
export function isJackpotHand(cards: Card[]): boolean {
  const ids = cards.map((c) => c % 52).sort((a, b) => a - b);
  return ids.length === 2 && ids[0] === 0 && ids[1] === 10;
}

export function isBlackjack(cards: Card[]): boolean {
  return cards.length === 2 && handTotal(cards).total === 21;
}

function draw(state: BlackjackState, rng: Rng): Card {
  if (state.shoe.length === 0) state.shoe = rng.shuffle(newDecks(DECKS));
  return state.shoe.pop()!;
}

function clone(state: BlackjackState): BlackjackState {
  return {
    ...state,
    shoe: [...state.shoe],
    dealer: [...state.dealer],
    hands: state.hands.map((h) => ({ ...h, cards: [...h.cards] })),
    results: [...state.results],
  };
}

function canDouble(hand: BjHand, bankroll: number): boolean {
  return hand.cards.length === 2 && !hand.splitAces && bankroll >= hand.bet;
}

function canSplit(state: BlackjackState, hand: BjHand, bankroll: number): boolean {
  return (
    state.hands.length === 1 &&
    hand.cards.length === 2 &&
    cardValue(hand.cards[0]!) === cardValue(hand.cards[1]!) &&
    bankroll >= hand.bet
  );
}

/** Moves play to the next unfinished hand, or plays out the dealer and
 *  settles once every hand is done. Returns the bankroll after settling. */
function advance(state: BlackjackState, bankroll: number, rng: Rng): ActResult<BlackjackState> {
  const next = state.hands.findIndex((h) => !h.done);
  if (next >= 0) {
    state.active = next;
    return { state, bankroll };
  }
  const anyLive = state.hands.some((h) => handTotal(h.cards).total <= 21);
  const frames: string[] = [];
  if (anyLive) {
    while (handTotal(state.dealer).total < 17) state.dealer.push(draw(state, rng));
    // The dealer's play, card by card: the hole card flips, then each hit.
    frames.push(...dealerFrames(state, state.dealer, 2));
    // The ★'s luck: when the dealer beats a live hand, sometimes the dealer's
    // hand is dealt again from the upcard.
    const beats = () => {
      const d = handTotal(state.dealer).total;
      return d <= 21 && state.hands.some((h) => handTotal(h.cards).total < d);
    };
    if (beats() && rng.next() < (SECOND_CHANCE[state.luck ?? 0] ?? 0)) {
      const first = state.dealer;
      state.dealer = [state.dealer[0]!];
      while (handTotal(state.dealer).total < 17) state.dealer.push(draw(state, rng));
      if (!beats()) state.lucky = true;
      // Every re-deal is shown: the hand that beat you, then the new one.
      frames.push(secondChanceFrame(`${tableView(state, first)}\nThe dealer re-deals...`), ...dealerFrames(state, state.dealer, 1));
    }
  }
  const dealerTotal = handTotal(state.dealer).total;
  let returned = 0;
  let wagered = 0;
  const results: string[] = [];
  state.hands.forEach((hand, i) => {
    wagered += hand.bet;
    const total = handTotal(hand.cards).total;
    const label = state.hands.length > 1 ? `Hand ${i + 1}: ` : '';
    if (total > 21) results.push(`${label}Bust -$${hand.bet}`);
    else if (dealerTotal > 21 || total > dealerTotal) {
      returned += hand.bet * 2;
      results.push(`${label}Win +$${hand.bet}`);
    } else if (total === dealerTotal) {
      returned += hand.bet;
      results.push(`${label}Push`);
    } else results.push(`${label}Lose -$${hand.bet}`);
  });
  state.phase = 'done';
  if (state.lucky && results[0]) results[0] = `★ ${results[0]}`;
  state.results = results;
  state.net = returned - wagered;
  return { state, bankroll: bankroll + returned, settled: { wagered, returned }, frames };
}

function deal(prev: BlackjackState, env: TableEnv): ActResult<BlackjackState> {
  const state = clone(prev);
  if (state.shoe.length < RESHUFFLE_BELOW) state.shoe = env.rng.shuffle(newDecks(DECKS));
  const bet = env.bet;
  const player = [draw(state, env.rng)];
  state.dealer = [draw(state, env.rng)];
  player.push(draw(state, env.rng));
  state.dealer.push(draw(state, env.rng));
  state.hands = [{ cards: player, bet, done: false, splitAces: false }];
  state.active = 0;
  state.results = [];
  state.net = 0;
  state.luck = luckOf(env);
  state.lucky = false;
  const bankroll = env.bankroll - bet;

  const playerBj = isBlackjack(player);
  // The ★'s luck can also save you from a dealer blackjack: a new hole card.
  const dealFrames: string[] = [];
  if (!playerBj && isBlackjack(state.dealer) && env.rng.next() < (SECOND_CHANCE[state.luck] ?? 0)) {
    dealFrames.push(secondChanceFrame(`${tableView(state, state.dealer)}\nDealer blackjack...`));
    state.dealer[1] = draw(state, env.rng);
    state.lucky = !isBlackjack(state.dealer);
  }
  const dealerBj = isBlackjack(state.dealer);
  if (playerBj || dealerBj) {
    state.phase = 'done';
    let returned = 0;
    if (playerBj && dealerBj) {
      returned = bet;
      state.results = ['Both blackjack. Push'];
    } else if (playerBj) {
      returned = bet + Math.floor(bet * 1.5);
      state.results = [`Blackjack! +$${returned - bet}`];
    } else state.results = [`Dealer blackjack -$${bet}`];
    state.net = returned - bet;
    return { state, bankroll: bankroll + returned, settled: { wagered: bet, returned }, jackpot: playerBj && isJackpotHand(player), frames: dealFrames.length ? dealFrames : undefined };
  }
  state.phase = 'player';
  return { state, bankroll, frames: dealFrames.length ? dealFrames : undefined };
}

function playerAct(prev: BlackjackState, optionId: string, env: TableEnv): ActResult<BlackjackState> {
  const state = clone(prev);
  const hand = state.hands[state.active];
  if (!hand) return { state: prev, bankroll: env.bankroll };
  let bankroll = env.bankroll;

  // The ★'s luck: a card that would bust you is sometimes swapped.
  const luckFrames: string[] = [];
  const bustSave = () => {
    if (handTotal(hand.cards).total <= 21 || env.rng.next() >= (SECOND_CHANCE[state.luck ?? 0] ?? 0)) return;
    luckFrames.push(secondChanceFrame(`${tableView(state, state.dealer.slice(0, 1))}\nThat would bust you...`));
    hand.cards[hand.cards.length - 1] = draw(state, env.rng);
    luckFrames.push(tableView(state, state.dealer.slice(0, 1)));
    if (handTotal(hand.cards).total <= 21) state.lucky = true;
  };
  if (optionId === 'hit') {
    hand.cards.push(draw(state, env.rng));
    bustSave();
    if (handTotal(hand.cards).total >= 21) hand.done = true;
  } else if (optionId === 'stand') {
    hand.done = true;
  } else if (optionId === 'double' && canDouble(hand, bankroll)) {
    bankroll -= hand.bet;
    hand.bet *= 2;
    hand.cards.push(draw(state, env.rng));
    bustSave();
    hand.done = true;
  } else if (optionId === 'split' && canSplit(state, hand, bankroll)) {
    bankroll -= hand.bet;
    const aces = rankOf(hand.cards[0]!) === 0;
    const second: BjHand = { cards: [hand.cards[1]!], bet: hand.bet, done: false, splitAces: aces };
    hand.cards = [hand.cards[0]!];
    hand.splitAces = aces;
    state.hands.push(second);
    for (const h of state.hands) {
      h.cards.push(draw(state, env.rng));
      if (h.splitAces || handTotal(h.cards).total === 21) h.done = true;
    }
  } else {
    return { state: prev, bankroll: env.bankroll };
  }
  const r = advance(state, bankroll, env.rng);
  const frames = [...luckFrames, ...(r.frames ?? [])];
  return frames.length ? { ...r, frames } : r;
}

/** The table with the dealer showing `dealer` (for animation frames). */
function tableView(state: BlackjackState, dealer: Card[]): string {
  return [`Dealer: ${handLine(dealer)}`, ...state.hands.map((h) => `You: ${handLine(h.cards)}`)].join('\n');
}

/** The dealer's hand appearing card by card, from `from` cards shown. */
function dealerFrames(state: BlackjackState, dealer: Card[], from: number): string[] {
  // Paced for suspense (user, 2026-09-23): each card holds for HOLD ticks,
  // and the last card — the one that makes the dealer stand or bust — gets
  // a "Dealer draws..." beat first. A usual dealer turn is ~1.25 s.
  const frames: string[] = [];
  for (let k = from; k <= dealer.length; k++) {
    if (k === dealer.length) {
      const before = k === 2 ? `Dealer: ${cardLabel(dealer[0]!)} ??` : `Dealer: ${handLine(dealer.slice(0, k - 1))}`;
      frames.push([before, ...state.hands.map((h) => `You: ${handLine(h.cards)}`), '', 'Dealer draws...'].join('\n'));
    }
    for (let i = 0; i < HOLD; i++) frames.push(tableView(state, dealer.slice(0, k)));
  }
  return frames;
}

/** Ticks each dealer card stays up before the next (a tick is 250 ms). */
const HOLD = 2;

function handLine(cards: Card[]): string {
  return `${cards.map(cardLabel).join(' ')} (${handTotal(cards).total})`;
}

export const blackjack: CasinoGame<BlackjackState> = {
  id: 'blackjack',
  usesLuck: true,
  name: 'Blackjack',
  blurb: 'Beat the dealer to 21. Blackjack pays 3:2.',
  baseMinBet: 5,
  baseMaxBet: 100,
  specialPerk: 'Luck: a card that would bust you may be swapped, and a dealer hand that beats you re-dealt.',
  jackpotRule: 'Blackjack with the A♠ and J♠.',
  create: () => ({ phase: 'idle', shoe: [], dealer: [], hands: [], active: 0, results: [], net: 0 }),
  locked: (state) => state.phase === 'player',
  options(state, env): TableOption[] {
    if (state.phase !== 'player') return [{ id: 'deal', label: state.phase === 'done' ? 'Deal again' : 'Deal' }];
    const hand = state.hands[state.active];
    const opts: TableOption[] = [
      { id: 'hit', label: 'Hit' },
      { id: 'stand', label: 'Stand' },
    ];
    if (hand && canDouble(hand, env.bankroll)) opts.push({ id: 'double', label: 'Double' });
    if (hand && canSplit(state, hand, env.bankroll)) opts.push({ id: 'split', label: 'Split' });
    return opts;
  },
  describe(state) {
    if (state.phase === 'idle') return 'Tap Deal to start a hand.';
    const dealer =
      state.phase === 'player' ? `Dealer: ${cardLabel(state.dealer[0]!)} ??` : `Dealer: ${handLine(state.dealer)}`;
    const hands = state.hands.map((h, i) => {
      const marker = state.phase === 'player' && state.hands.length > 1 && i === state.active ? '>' : '';
      return `${marker}You: ${handLine(h.cards)}`;
    });
    const lines = [dealer, ...hands];
    if (state.phase === 'done') lines.push('', ...state.results);
    return lines.join('\n');
  },
  act(state, optionId, env) {
    if (optionId === 'deal' && state.phase !== 'player') return deal(state, env);
    if (state.phase === 'player') return playerAct(state, optionId, env);
    return { state, bankroll: env.bankroll };
  },
};
